import {omneticApi} from '@dms/api/base';

import {getRandomId} from 'shared/web';

import {
  AclScopeOptionResponseBodyAccessControlList,
  AclScopeResponseBodyAccessControlList,
  GrantActionPermissionApiArgAccessControlList,
  GrantActionPermissionApiResponseAccessControlList,
  GrantAllPermissionApiArgAccessControlList,
  GrantAllPermissionApiResAccessControlList,
  GrantFieldPermissionApiArgAccessControlList,
  GrantFieldPermissionApiResponseAccessControlList,
  GrantPermissionApiArgAccessControlList,
  GrantPermissionApiResponseAccessControlList,
  GrantResourcePermissionApiArgAccessControlList,
  GrantResourcePermissionApiResponseAccessControlList,
  ListPermissionExpressionApiArgAccessControlList,
  ListPermissionExpressionApiResponseAccessControlList,
  ListPermissionsApiArgAccessControlList,
  ListPermissionsApiResponseAccessControlList,
  ListPermissionsV2ApiArgAccessControlList,
  ListPermissionsV2ApiResponseAccessControlList,
  ListProtectedUnitsApiArgAccessControlList,
  ListProtectedUnitsApiResponseAccessControlList,
  ListResourcesApiArgAccessControlList,
  ListResourcesApiResponseAccessControlList,
  ListResourceScopeOptionsApiArgAccessControlList,
  ListResourceScopeOptionsApiResponseAccessControlList,
  ListUserAllowedRecordActionsApiArgAccessControlList,
  ListUserAllowedRecordActionsApiResponseAccessControlList,
  ListUserAllowedResourceActionsApiArgAccessControlList,
  ListUserAllowedResourceActionsApiResponseAccessControlList,
  PermissionV2ResponseBodyAccessControlList,
  RevokeAllPermissionApiArgAccessControlList,
  RevokeAllPermissionApiResAccessControlList,
  RevokePermissionApiArgAccessControlList,
  RevokePermissionApiResponseAccessControlList,
  RevokeResourcePermissionApiArgAccessControlList,
  RevokeResourcePermissionApiResAccessControlList,
  TransformedAclScopeResponseBodyAccessControlList,
  TransformedListResourcesApiResponseAccessControlList,
  TransformedListResourceScopeOptionsApiResponseAccessControlList,
  TransformedResourceResponseBodyAccessControlList,
  UpdateActionPermissionApiArgAccessControlList,
  UpdateActionPermissionApiResAccessControlList,
  UpdateFieldPermissionApiArgAccessControlList,
  UpdateFieldPermissionApiResAccessControlList,
  UpdateProtectedUnitPermissionV2ApiArgAccessControlList,
  UpdateProtectedUnitPermissionV2ApiResponseAccessControlList,
} from './types';

export const accessControlListApi = omneticApi.injectEndpoints({
  endpoints: (build) => ({
    listResources: build.query<
      TransformedListResourcesApiResponseAccessControlList,
      ListResourcesApiArgAccessControlList
    >({
      query: () => ({
        url: `/dms/v1/acl/resource`,
      }),
      transformResponse: (
        response: ListResourcesApiResponseAccessControlList
      ): TransformedListResourcesApiResponseAccessControlList => {
        function transformResources(
          resources: ListResourcesApiResponseAccessControlList
        ): TransformedResourceResponseBodyAccessControlList[] {
          return resources.map(
            (resource) =>
              ({
                ...resource,
                subResources: transformResources(resource.subResources),

                scopes: resource.scopes.map(
                  (scope: AclScopeResponseBodyAccessControlList) =>
                    ({
                      value: scope.id,
                      label: scope.name,
                    }) as TransformedAclScopeResponseBodyAccessControlList
                ),
              }) as TransformedResourceResponseBodyAccessControlList
          );
        }

        return transformResources(response);
      },
    }),
    listResourceScopeOptions: build.query<
      TransformedListResourceScopeOptionsApiResponseAccessControlList,
      ListResourceScopeOptionsApiArgAccessControlList
    >({
      query: (queryArg) => ({
        url: `/dms/v1/acl/resource/${queryArg.resourceId}/scope/${queryArg.scopeId}/options`,
      }),

      transformResponse: (
        response: ListResourceScopeOptionsApiResponseAccessControlList
      ): TransformedListResourceScopeOptionsApiResponseAccessControlList =>
        response.map((option: AclScopeOptionResponseBodyAccessControlList) => ({
          value: option.id,
          label: option.name,
        })),
    }),
    listPermissions: build.query<
      ListPermissionsApiResponseAccessControlList,
      ListPermissionsApiArgAccessControlList
    >({
      query: (queryArg) => ({
        url: `/dms/v1/acl/permission`,
        params: {roleId: queryArg.roleId},
      }),
      providesTags: ['permissions'],
    }),
    grantActionPermission: build.mutation<
      GrantActionPermissionApiResponseAccessControlList,
      GrantActionPermissionApiArgAccessControlList
    >({
      query: (body) => ({
        url: `/dms/v1/acl/permission/action`,
        method: 'POST',
        body,
      }),
      async onQueryStarted({actionId, resourceId, roleId}, {dispatch, queryFulfilled}) {
        const patchResult = dispatch(
          accessControlListApi.util.updateQueryData('listPermissionsV2', {roleId}, (draft) => {
            const additionalItem: PermissionV2ResponseBodyAccessControlList = {
              protectedUnit: {id: actionId, name: ''},
              group: {id: resourceId, name: ''},
              roleId,
              scopes: [],
              id: getRandomId(),
            };

            return [...draft, additionalItem];
          })
        );

        await queryFulfilled.catch(patchResult.undo);
      },
      invalidatesTags: ['permissions'],
    }),
    grantFieldPermission: build.mutation<
      GrantFieldPermissionApiResponseAccessControlList,
      GrantFieldPermissionApiArgAccessControlList
    >({
      query: (body) => ({
        url: `/dms/v1/acl/permission/field`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['permissions'],
    }),
    updateFieldPermission: build.mutation<
      UpdateFieldPermissionApiResAccessControlList,
      UpdateFieldPermissionApiArgAccessControlList
    >({
      query: (body) => ({
        url: `/dms/v1/acl/permission/field`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['permissions'],
    }),
    updateActionPermission: build.mutation<
      UpdateActionPermissionApiResAccessControlList,
      UpdateActionPermissionApiArgAccessControlList
    >({
      query: (body) => ({
        url: `/dms/v1/acl/permission/action`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['permissions'],
    }),
    grantResourcePermission: build.mutation<
      GrantResourcePermissionApiResponseAccessControlList,
      GrantResourcePermissionApiArgAccessControlList
    >({
      query: (body) => ({
        url: `/dms/v1/acl/permission/resource`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['permissions'],
    }),
    revokeAllPermission: build.mutation<
      RevokeAllPermissionApiResAccessControlList,
      RevokeAllPermissionApiArgAccessControlList
    >({
      query: (params) => ({
        url: `/dms/v1/acl/permission/all`,
        method: 'DELETE',
        params,
      }),
      invalidatesTags: ['permissions'],
    }),
    grantAllPermission: build.mutation<
      GrantAllPermissionApiResAccessControlList,
      GrantAllPermissionApiArgAccessControlList
    >({
      query: (body) => ({
        url: `/dms/v1/acl/permission/all`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['permissions'],
    }),
    revokeResourcePermission: build.mutation<
      RevokeResourcePermissionApiResAccessControlList,
      RevokeResourcePermissionApiArgAccessControlList
    >({
      query: (params) => ({
        url: `/dms/v1/acl/permission/resource`,
        method: 'DELETE',
        params,
      }),
      invalidatesTags: ['permissions'],
    }),
    revokePermission: build.mutation<
      RevokePermissionApiResponseAccessControlList,
      RevokePermissionApiArgAccessControlList
    >({
      query: (queryArg) => ({
        url: `/dms/v1/acl/permission/${queryArg.id}`,
        method: 'DELETE',
      }),
      async onQueryStarted({id, roleId}, {dispatch, queryFulfilled}) {
        const patchResult = dispatch(
          accessControlListApi.util.updateQueryData('listPermissionsV2', {roleId}, (draft) =>
            draft.filter((item) => item.id !== id)
          )
        );

        await queryFulfilled.catch(patchResult.undo);
      },
      invalidatesTags: ['permissions'],
    }),
    listUserAllowedResourceActions: build.query<
      ListUserAllowedResourceActionsApiResponseAccessControlList,
      ListUserAllowedResourceActionsApiArgAccessControlList
    >({
      query: () => ({
        url: `/dms/v1/acl/user/allowed-actions/resource`,
      }),
    }),
    listUserAllowedRecordActions: build.query<
      ListUserAllowedRecordActionsApiResponseAccessControlList,
      ListUserAllowedRecordActionsApiArgAccessControlList
    >({
      query: (queryArg) => ({
        url: `/dms/v1/acl/user/allowed-actions/resource/${queryArg.resourceId}/record/${queryArg.recordId}`,
      }),
    }),
    listPermissionsV2: build.query<
      ListPermissionsV2ApiResponseAccessControlList,
      ListPermissionsV2ApiArgAccessControlList
    >({
      query: (queryArg) => ({
        url: `/dms/v2/acl/permission`,
        params: {roleId: queryArg.roleId},
      }),
      providesTags: ['permissions'],
    }),
    grantPermission: build.mutation<
      GrantPermissionApiResponseAccessControlList,
      GrantPermissionApiArgAccessControlList
    >({
      query: (queryArg) => ({
        url: `/dms/v2/acl/permission`,
        method: 'POST',
        body: queryArg.grantPermissionRequestBody,
      }),
      invalidatesTags: ['permissions'],
    }),
    updateProtectedUnitPermission: build.mutation<
      UpdateProtectedUnitPermissionV2ApiResponseAccessControlList,
      UpdateProtectedUnitPermissionV2ApiArgAccessControlList
    >({
      query: (queryArg) => ({
        url: `/dms/v2/acl/permission/${queryArg.permissionId}`,
        method: 'PUT',
        body: queryArg.updateActionPermissionRequestBody,
      }),
      invalidatesTags: ['permissions'],
    }),
    listPermissionExpression: build.query<
      ListPermissionExpressionApiResponseAccessControlList,
      ListPermissionExpressionApiArgAccessControlList
    >({
      query: () => ({
        url: `/dms/v1/acl/user/permission-expression`,
      }),
      providesTags: ['permissions'],
    }),
    listProtectedUnits: build.query<
      ListProtectedUnitsApiResponseAccessControlList,
      ListProtectedUnitsApiArgAccessControlList
    >({
      query: () => ({
        url: `/dms/v1/acl/protected-unit`,
      }),
      providesTags: ['permissions'],
    }),
  }),
});

export const {
  useListPermissionExpressionQuery,
  useListPermissionsV2Query,
  useListProtectedUnitsQuery,
  useUpdateProtectedUnitPermissionMutation,
  useGrantPermissionMutation,
  useGrantAllPermissionMutation,
  useRevokeAllPermissionMutation,
  useUpdateActionPermissionMutation,
  useUpdateFieldPermissionMutation,
  useListResourcesQuery,
  useListResourceScopeOptionsQuery,
  useLazyListResourceScopeOptionsQuery,
  useListPermissionsQuery,
  useGrantActionPermissionMutation,
  useGrantFieldPermissionMutation,
  useGrantResourcePermissionMutation,
  useRevokeResourcePermissionMutation,
  useRevokePermissionMutation,
  useListUserAllowedResourceActionsQuery,
  useListUserAllowedRecordActionsQuery,
  useLazyListUserAllowedRecordActionsQuery,
} = accessControlListApi;
